import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_LOCALE, MatNativeDateModule } from '@angular/material/core';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MarkdownModule, MERMAID_OPTIONS } from 'ngx-markdown';
import { AngularMarkdownEditorModule } from 'angular-markdown-editor';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { RestService } from '@services/rest/rest.service';
import { DialogService } from '@services/dialog/dialog.service';

import { EnsureLinkPrefixPipe } from './pipes/ensure-link-prefix.pipe';
import { MapValuesPipe } from './pipes/map-values.pipe';

import { SmartImageComponent } from './components/smart-image/smart-image.component';
import { MenuItemSelectorComponent } from './components/menu-item-selector/menu-item-selector.component';
import { PageActionsComponent } from './components/page-actions/page-actions.component';
import { PageToolbarComponent } from './components/page-toolbar/page-toolbar.component';
import { PageNotFoundComponent } from './pages/page-not-found/page-not-found.component';
import { ConfirmDialogComponent } from './dialogs/confirm-dialog/confirm-dialog.component';
import { GeneratorDialogComponent } from './dialogs/generator-dialog/generator-dialog.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { CustomersComponent } from './pages/customers/customers.component';
import { CustomerDetailComponent } from './pages/customers/customer-detail/customer-detail.component';
import { AuthenticationComponent } from './pages/authentication/authentication.component';
import { AuthenticationGuard } from './guards/authentication.guard';
import { TokenGuard } from './guards/token.guard';
import { ImportsComponent } from './pages/imports/imports.component';
import { ImportTypesComponent } from './pages/import-types/import-types.component';
import { ImportTypesDetailComponent } from './pages/import-types/import-types-detail/import-types-detail.component';
import { ScreenListComponent } from './pages/screens/screen-list/screen-list.component';
import { PageContentComponent } from './components/page-content/page-content.component';
import { CompositionsComponent } from './pages/compositions/compositions.component';
import { CompositionWizardComponent } from './pages/compositions/composition-wizard/composition-wizard.component';
import { CompositionWizardPackageComponent } from './pages/compositions/composition-wizard/composition-wizard-package/composition-wizard-package.component';
import { CompositionWizardScreenComponent } from './pages/compositions/composition-wizard/composition-wizard-screen/composition-wizard-screen.component';
import { CompositionWizardImportsComponent } from './pages/compositions/composition-wizard/composition-wizard-imports/composition-wizard-imports.component';
import { DataTableComponent } from './components/data-table/data-table.component';
import { CompositionDetailComponent } from './pages/compositions/composition-detail/composition-detail.component';
import { CompositionAppEditorComponent } from './pages/compositions/composition-app-editor/composition-app-editor.component';
import { CompositionAppDetailComponent } from './pages/compositions/composition-app-detail/composition-app-detail.component';
import { CompositionAppEditorMenuItemsComponent } from './pages/compositions/composition-app-editor/composition-app-editor-menu-items/composition-app-editor-menu-items.component';
import { PackageListComponent } from './pages/packages/package-list/package-list.component';
import { PackageEditorComponent } from './pages/packages/package-editor/package-editor.component';

import { PackageInstalledAppsManagerComponent } from './pages/packages/package-installed-apps-manager/package-installed-apps-manager.component';
import { AppPickerDialogComponent } from './pages/packages/package-installed-apps-manager/app-picker-dialog/app-picker-dialog.component';
import { PackageInstalledAppEditorComponent } from './pages/packages/package-installed-apps-manager/package-installed-app-editor/package-installed-app-editor.component';

import { CompositionWizardCodeSnippetsComponent } from './pages/compositions/composition-wizard/composition-wizard-code-snippets/composition-wizard-code-snippets.component';
import { PageHeaderComponent } from './components/page-header/page-header.component';
import { ImportTypesEditComponent } from './pages/import-types/import-types-edit/import-types-edit.component';
import { ScreenDetailComponent } from './pages/screens/screen-detail/screen-detail.component';
import { ErrorlogComponent } from './pages/errorlog/errorlog.component';
import { FailedImportsComponent } from './pages/failed-imports/failed-imports.component';
import { RedirectsComponent } from './pages/redirects/redirects.component';
import { RedirectDetailComponent } from './pages/redirects/redirect-detail/redirect-detail.component';
import { ImportsDetailComponent } from './pages/imports/imports-detail/imports-detail.component';
import { ImportLogsComponent } from './pages/import-logs/import-logs.component';
import { ImportLogsDetailComponent } from './pages/import-logs/import-logs-detail/import-logs-detail.component';
import { JsonContentTableComponent } from './components/json-content-table/json-content-table.component';
import { MessageBubbleComponent } from './components/message-bubble/message-bubble.component';
import { RedirectEditComponent } from './pages/redirects/redirect-edit/redirect-edit.component';
import { ScreenSettingsComponent } from './pages/screen-settings/screen-settings.component';
import { KueComponent } from './pages/kue/kue.component';
import { ImportEditComponent } from './pages/imports/import-edit/import-edit.component';
import { DefaultFormComponent } from './components/default-form/default-form.component';
import { FormDirective } from './directives/form/form.directive';
import { KeysPipe } from './pipes/keys.pipe';
import { JsonStringifyPipe } from './pipes/json-stringify.pipe';
import { JsonAreaComponent } from './components/json-area/json-area.component';
import { CodeAreaComponent } from './components/code-area/code-area.component';
import { SmartInputComponent } from './components/smart-input/smart-input.component';
import { DurationInputComponent } from './components/duration-input/duration-input.component';
import { ImportCreateComponent } from './pages/imports/import-create/import-create.component';
import { StatusBarComponent } from './components/status-bar/status-bar.component';
import { RedirectLogComponent } from './pages/redirects/redirect-log/redirect-log.component';
import { ImportTypesCreateComponent } from './pages/import-types/import-types-create/import-types-create.component';
import { CodeSnippetsComponent } from './pages/code-snippets/code-snippets.component';
import { CodeSnippetsDetailComponent } from './pages/code-snippets/code-snippets-detail/code-snippets-detail.component';
import { CodeSnippetsEditComponent } from './pages/code-snippets/code-snippets-edit/code-snippets-edit.component';
import { LegacyCompositionComponent } from './pages/legacy-composition/legacy-composition.component';
import { LegacyCompositionEditComponent } from './pages/legacy-composition/legacy-composition-edit/legacy-composition-edit.component';
import { LegacyCompositionDetailComponent } from './pages/legacy-composition/legacy-composition-detail/legacy-composition-detail.component';
import { MessengerService } from '@services/messenger/messenger.service';
import { StorageService } from '@services/storage/storage.service';
import { LoginComponent } from './pages/authentication/login/login.component';
import { RegisterComponent } from './pages/authentication/register/register.component';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { OrganisationsComponent } from './pages/organisations/organisations.component';
import { OrganisationEditComponent } from './pages/organisations/organisation-edit/organisation-edit.component';
import { PrivilegesComponent } from './pages/privileges/privileges.component';
import { PrivilegeEditComponent } from './pages/privileges/privilege-edit/privilege-edit.component';
import { RolesComponent } from './pages/roles/roles.component';
import { RoleEditComponent } from './pages/roles/role-edit/role-edit.component';
import { OrganisationDetailComponent } from './pages/organisations/organisation-detail/organisation-detail.component';
import { ProfileEditComponent } from './pages/profiles/profile-edit/profile-edit.component';
import { ProfileDetailComponent } from './pages/profiles/profile-detail/profile-detail.component';
import { RolePrivilegeManagerComponent } from './pages/roles/role-privilege-manager/role-privilege-manager.component';
import { RolePrivilegeSelectionComponent } from './pages/roles/role-privilege-manager/role-privilege-selection/role-privilege-selection.component';
import { RoleDetailComponent } from './pages/roles/role-detail/role-detail.component';
import { ProfilesComponent } from './pages/profiles/profiles.component';
import { OrganisationSelectorComponent } from './pages/organisation-selector/organisation-selector.component';
import { GroupsComponent } from './pages/groups/groups.component';
import { GroupDetailComponent } from './pages/groups/group-detail/group-detail.component';
import { GroupEditComponent } from './pages/groups/group-edit/group-edit.component';
import { GroupRolesManagerComponent } from './pages/groups/group-roles-manager/group-roles-manager.component';
import { GroupRoleSelectionComponent } from './pages/groups/group-roles-manager/group-role-selection/group-role-selection.component';
import { GroupParticipantsManagerComponent } from './pages/groups/group-participants-manager/group-participants-manager.component';
import { OrganisationGuard } from './guards/organisation.guard';
import { SelectOrganisationComponent } from './pages/select-organisation/select-organisation.component';
import { ScreenSettingsEditComponent } from './pages/screen-settings/screen-settings-edit/screen-settings-edit.component';
import { CustomerEditComponent } from './pages/customers/customer-edit/customer-edit.component';
import { JsonEditComponent } from './pages/jsons/json-edit/json-edit.component';
import { ScreenSelectorComponent } from './pages/screens/screen-selector/screen-selector.component';
import { JsonReadOnlyComponent } from './pages/jsons/json-read-only/json-read-only.component';
import { PrivilegeGuard } from './guards/privilege.guard'
import { IfPrivilegeDirective } from './directives/if-privilege/if-privilege.directive';

import { DraggableDirective } from './directives/draggable/draggable.directive';
import { CroppingDirective } from './directives/draggable/cropping.directive';
import { ResetComponent } from './pages/authentication/reset/reset.component';
import { PageNotAuthComponent } from './pages/page-not-auth/page-not-auth.component';
import { MessageComponent } from './pages/authentication/message/message.component';
import { ErrorHandlingService } from '@services/error-handling/error-handling.service';
import { ImageAssetSelectorComponent } from './components/image-asset-selector/image-asset-selector.component';
import { AssetSelectorComponent } from './components/asset-selector/asset-selector.component';
import { TransformDatePipe } from './pipes/transform-date.pipe';
import { TranslatePipe } from './pipes/translate.pipe';
import { TranslateService } from '@services/translate/translate.service';
import { MyProfileComponent } from './pages/my-profile/my-profile.component';
import { MyOrganisationService } from '@services/organisations/my-organisation.service';

import { FrontendErrorService } from '@services/frontend-error/frontend-error.service';
import { FrontendErrorlogComponent } from './pages/frontend-errorlog/frontend-errorlog.component';
import { FrontendErrorlogDetailComponent } from './pages/frontend-errorlog/frontend-errorlog-detail/frontend-errorlog-detail.component';
import { ErrorlogDetailComponent } from './pages/errorlog/errorlog-detail/errorlog-detail.component';
import { DynamicContentComponent } from './pages/dynamic-content/dynamic-content.component';
import { DynamicContentDetailComponent } from './pages/dynamic-content/dynamic-content-detail/dynamic-content-detail.component';
import { DynamicContentEditComponent } from './pages/dynamic-content/dynamic-content-edit/dynamic-content-edit.component';
import { ReadmeComponent } from './components/readme/readme.component';
import { OpenReadmeService } from '@services/readme/open-readme.service';
import { PasswordConfirmComponent } from './components/password-confirm/password-confirm.component';
import { ImageEditorComponent } from './components/image-editor/image-editor.component';
import { ImportDataExplorerComponent } from './pages/import-data-explorer/import-data-explorer.component';
import { NgxJsonViewerModule } from 'ngx-json-viewer';

import { ResourcesComponent } from './pages/resources/resources.component';
import { AssetsComponent } from './components/assets/assets.component';
import { SafePipe } from './pipes/safe.pipe';
import { CustomersListComponent } from './pages/customers-list/customers-list.component';
import { SmartInputHintComponent } from './components/smart-input-hint/smart-input-hint.component';
import { DayMonthInputComponent } from './components/smart-input/specials/day-month-input/day-month-input.component';
import { PendingChangesGuard } from './guards/pending-changes.guard';
import { PackageSeasonalImagesComponent } from './pages/packages/package-seasonal-images/package-seasonal-images.component';
import {
  CompositionWizardDynamicEntranceComponent
} from './pages/compositions/composition-wizard/composition-wizard-dynamic-entrance/composition-wizard-dynamic-entrance.component';
import {
  CompositionWizardCustomerSelectComponent
} from './pages/compositions/composition-wizard/composition-wizard-dynamic-entrance/composition-wizard-customer-select/composition-wizard-customer-select.component';
import { ResetRedirectComponent } from './pages/authentication/reset/reset-redirect/reset-redirect.component';
import { LanguageSelectorComponent } from './components/language-selector/language-selector.component';
import { FileSizePipe } from './pipes/file-size.pipe';
import { QuillModule } from 'ngx-quill';
import { TimePickerComponent } from './components/time-picker/time-picker.component';
import { PackagesComponent } from './pages/packages/packages/packages.component';
import { DashboardMessageComponent } from './pages/dashboard-message/dashboard-message.component';
import { DashboardMessageEditComponent } from './pages/dashboard-message/dashboard-message-edit/dashboard-message-edit.component';
import { DashboardMessageItemComponent } from './components/dashboard-messasge-item/dashboard-message-item.component';
import { ImporterMonitorComponent } from './pages/importer-monitor/importer-monitor.component';
import { ImportConnectionComponent } from './pages/imports/import-connection/import-connection.component';
import { GroupSelectionComponent } from './components/popUpSelects/group-selection/group-selection.component';
import { CustomerSelectionComponent } from './components/popUpSelects/customer-selection/customer-selection.component';
import { OrganisationCustomerComponent } from './pages/organisation-customer/organisation-customer.component';
import { OrganisationCustomerDetailComponent } from './pages/organisation-customer/organisation-customer-detail/organisation-customer-detail.component';
import { OrganisationCustomerEditComponent } from './pages/organisation-customer/organisation-customer-edit/organisation-customer-edit.component';
import { DynamicContentEntrancesComponent } from './pages/dynamic-content-entrances/dynamic-content-entrances.component';
import { OrphansComponent } from './pages/orphans/orphans.component';
import { UrlShortenerComponent } from './pages/url-shortener/url-shortener.component';
import { CompositionAppDetailMenuItemsComponent } from './pages/compositions/composition-app-detail/composition-app-detail-menu-items/composition-app-detail-menu-items.component';
import { PackageGeneralComponent } from './pages/packages/package-general/package-general.component';
import { PackageScreenImagesComponent } from './pages/packages/package-screen-images/package-screen-images.component';
import { PackageInstalledWidgetEditorComponent } from './pages/packages/package-installed-apps-manager/package-installed-widget-editor/package-installed-widget-editor.component';
import { ContactComponent } from './pages/dynamic-content/dynamic-content-edit/components/contact/contact.component';
import { PublishingComponent } from './pages/dynamic-content/dynamic-content-edit/components/publishing/publishing.component';
import { MediaComponent } from './pages/dynamic-content/dynamic-content-edit/components/media/media.component';
import { DocumentsComponent } from './pages/dynamic-content/dynamic-content-edit/components/documents/documents.component';
import { CodeSnippetsContentComponent } from './pages/dynamic-content/dynamic-content-edit/components/code-snippets/code-snippets.component';
import { OpeningHoursComponent } from './pages/dynamic-content/dynamic-content-edit/components/opening-hours/opening-hours.component';
import { SlideItemsComponent } from './pages/dynamic-content/dynamic-content-edit/components/slide-items/slide-items.component';
import { SlideConfigComponent } from './pages/dynamic-content/dynamic-content-edit/components/slide-items/slide-config/slide-config.component';
import { ReadmesComponent } from './pages/readmes/readmes.component';
import { ReadmesEditComponent } from './pages/readmes/readmes-edit/readmes-edit.component';
import { ImporterSelectorComponent } from './components/importer-selector/importer-selector.component';
import { LanguageDialogComponent } from './components/language-dialog/language-dialog.component';
import { EnvironmentService } from '@services/environment/environment.service';
import { CleanUpQuillComponent } from './pages/clean-up-quill/clean-up-quill.component';
import { FailedPackagesComponent } from './pages/failed-packages/failed-packages.component';
import { FailedPackagesDetailComponent } from './pages/failed-packages/failed-packages-detail/failed-packages-detail.component';
import { ExternalTokensComponent } from './pages/external-tokens/external-tokens.component';
import { ExternalTokensEditComponent } from './pages/external-tokens/external-tokens-edit/external-tokens-edit.component';
import { PipeImportsComponent } from './pages/pipe-imports/pipe-imports.component';
import { PipeImportEditComponent } from './pages/pipe-imports/pipe-import-edit/pipe-import-edit.component';
import { PipeImportDetailComponent } from './pages/pipe-imports/pipe-import-detail/pipe-import-detail.component';
import { SpeedDialFabComponent } from './components/speed-dial-fab/speed-dial-fab.component';
import { PipeImportLogsDetailComponent } from './pages/pipe-imports/pipe-import-logs/pipe-import-logs-detail/pipe-import-logs-detail.component';
import { PipeImportLogsComponent } from './pages/pipe-imports/pipe-import-logs/pipe-import-logs.component';
import { FailedPipeImportsComponent } from './pages/failed-pipe-imports/failed-pipe-imports.component';
import { OrganisationSelectionComponent } from './components/popUpSelects/organisation-selection/organisation-selection.component';
import { DynamicContentTableComponent } from './components/dynamic-content-table/dynamic-content-table.component';
import { CodeSnippetsScreenSelectComponent } from './pages/code-snippets/code-snippets-detail/code-snippets-screen-select/code-snippets-screen-select.component';
import { AppHighscoreResetDialogComponent } from './pages/packages/package-installed-apps-manager/app-highscore-reset-dialog/app-highscore-reset-dialog.component';
import { PackageEraserComponent } from './pages/packages/package-editor/package-eraser/package-eraser.component';
import { OrganisationEraserComponent } from './pages/organisations/organisation-edit/organisation-eraser/organisation-eraser.component';
import { CustomerEraserComponent } from './pages/customers/customer-detail/customer-eraser/customer-eraser.component';
import { GroupContentManagerComponent } from './pages/groups/group-content-manager/group-content-manager.component';
import { DynamicContentEntranceSelectionComponent } from './pages/groups/group-content-manager/dynamic-content-entrance-selection/dynamic-content-entrance-selection.component';
import { IconSelectorComponent } from './components/icon-selector/icon-selector.component';
import { IconSelectionDialogComponent } from './components/icon-selector/icon-selection-dialog/icon-selection-dialog.component';
import { FrontProfilesComponent } from './pages/front-profiles/front-profiles.component';
import { FrontProfileEditComponent } from './pages/front-profiles/front-profile-edit/front-profile-edit.component';
import { GroupSecuredAppManagerComponent } from './pages/groups/group-secured-app-manager/group-secured-app-manager.component';
import { PackageInstalledAppSelectionComponent } from './pages/groups/group-secured-app-manager/package-installed-app-selection/package-installed-app-selection.component';
import { PackageLegalComponent } from './pages/packages/package-legal/package-legal.component';
import { Nl2BrPipe } from './pipes/nl-2-br.pipe';
import { BannerComponent } from './pages/banner/banner.component';
import { BannerEditComponent } from './pages/banner/banner-edit/banner-edit.component';
import { BannerDetailComponent } from './pages/banner/banner-detail/banner-detail.component';
import { DashboardDataDialogComponent } from './components/dashboard-data-dialog/dashboard-data-dialog.component';
import { QrcodeDialogComponent } from './dialogs/qrcode-dialog/qrcode-dialog.component';
import { MyOrganizationComponent } from './pages/my-organization/my-organization.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { TranslateDatePipe } from './pipes/translate-date.pipe';
import { IbexiconComponent } from './pages/ibexicon/ibexicon.component';
import { IbexIconModule } from '@ibexfusion/ibex-icon';
import { FrontGroupEditComponent } from './pages/front-profiles/front-group-edit/front-group-edit.component';
import { FrontProfileSelectionComponent } from './components/popUpSelects/front-profile-selection/front-profile-selection.component';
import { TranslationComponent } from './pages/translation/translation.component';
import { TranslationEditComponent } from './pages/translation/translation-edit/translation-edit.component';
import { TranslationDetailComponent } from './pages/translation/translation-detail/translation-detail.component';
import { TimelineChartComponent } from './components/analytics/timeline-chart/timeline-chart.component';
import { BarChartComponent } from './components/analytics/bar-chart/bar-chart.component';
import { AnalyticsComponent } from './pages/analytics/analytics.component';
import { MatPaginatorModule } from '@angular/material/paginator';
import { PieChartComponent } from './components/analytics/pie-chart/pie-chart.component';
import { TitleSelectionComponent } from './components/popUpSelects/title-selection/title-selection.component';
import { ApplicationGenerationComponent } from './components/application-generation/application-generation.component';
import { CustomerWizardComponent } from './pages/customer-wizard/customer-wizard.component';
import { CustomerWizardEditComponent } from './pages/customer-wizard/customer-wizard-edit/customer-wizard-edit.component';
import { AnalyticsDetailComponent } from './pages/analytics/analytics-detail/analytics-detail.component';
import { SubmenuCreatorComponent } from './components/submenu-creator/submenu-creator.component';
import { CompositionImportsComponent } from './pages/compositions/composition-imports/composition-imports.component';
import { PackageEditorIbexsmartComponent } from './pages/packages/package-editor-ibexsmart/package-editor-ibexsmart.component';
import { PackageEditorIbextouchComponent } from './pages/packages/package-editor-ibextouch/package-editor-ibextouch.component';
import { ColumnChartComponent } from './components/analytics/column-chart/column-chart.component';
import { PackageEditorIbexinfoComponent } from './pages/packages/package-editor-ibexinfo/package-editor-ibexinfo.component';
import { PackageEditorIbextabletComponent } from './pages/packages/package-editor-ibextablet/package-editor-ibextablet.component';
import { FrontProfileExportWizardComponent } from './components/popUpSelects/front-profile-export-wizard/front-profile-export-wizard.component';
import { FrontLabelEditComponent } from './pages/front-profiles/front-label-edit/front-label-edit.component';
import { CleanupCustomerComponent } from './pages/cleanup-customer/cleanup-customer.component';
import { CleanupCustomerExecuteComponent } from './pages/cleanup-customer/execute/cleanup-customer-execute.component';
import { PasswordComponent } from './components/password/password.component';
import { UserAdminComponent } from './pages/user-admin/user-admin.component';
import { UserAdminDetailComponent } from './pages/user-admin/user-admin-detail/user-admin-detail.component';
import { UserAdminEditComponent } from './pages/user-admin/user-admin-edit/user-admin-edit.component';
import { AddItemsFromList } from './pages/user-admin/user-admin-edit/dialogs/add-items-from-list/add-items-from-list.component';
import { DynamicContentTableRowComponent } from './components/dynamic-content-table/dynamic-content-table-row/dynamic-content-table-row.component';
import { HighlightSearchPipe } from './pipes/highlightSearchPipe.pipe';
import { TitleTranslateComponent } from './components/title-translate/title-translate.component';
import { AddNewProfile } from './pages/user-admin/user-admin-edit/dialogs/add-new-profile/add-new-profile.component';
import { ContactAndAddressComponent } from './pages/contact-and-address/contact-and-address.component';
import { ContactAndAddressEditComponent } from './pages/contact-and-address/contact-and-address-edit/contact-and-address-edit.component';
import { IbexCheckboxComponent } from './components/ibex-checkbox/ibex-checkbox.component';
import { ActivityComponent } from './pages/dynamic-content/dynamic-content-edit/components/activity/activity.component';
import { PersonComponent } from './pages/dynamic-content/dynamic-content-edit/components/person/person.component';
import { FinishVotingDialogComponent } from './pages/dynamic-content/dynamic-content-edit/components/voting/finish-voting-dialog/finish-voting-dialog.component';
import { VotingComponent } from './pages/dynamic-content/dynamic-content-edit/components/voting/voting.component';
import { ImportantNumbersComponent } from './pages/dynamic-content/dynamic-content-edit/components/important-numbers/important-numbers.component';
import { LogoComponent } from './pages/dynamic-content/dynamic-content-edit/components/logo/logo.component';
import { WebcamComponent } from './pages/dynamic-content/dynamic-content-edit/components/webcam/webcam.component';
import { QuizComponent } from './pages/dynamic-content/dynamic-content-edit/components/quiz/quiz.component';
import { FeedbackComponent } from './pages/dynamic-content/dynamic-content-edit/components/feedback/feedback.component';
import { GastronomyComponent } from './pages/dynamic-content/dynamic-content-edit/components/gastronomy/gastronomy.component';
import { EditMenuDialogComponent } from './pages/dynamic-content/dynamic-content-edit/components/gastronomy/edit-menu-dialog/edit-menu-dialog.component';
import {
  DynamicContentTableActiveStateComponent
} from './components/dynamic-content-table/dynamic-content-table-row/dynamic-content-table-active-state/dynamic-content-table-active-state.component';
import { NavigationComponent } from './pages/navigation/navigation.component';
import { EditAppDialogComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/edit-app-dialog.component';
import { IbextouchAppComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/forms/ibextouch-app/ibextouch-app.component';
import { IbexsmartAppComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/forms/ibexsmart-app/ibexsmart-app.component';
import { IbextouchWidgetComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/forms/ibextouch-widget/ibextouch-widget.component';
import { NgDetailViewModule } from '@ibexfusion/ibextouch-detail';
import { PreviewComponent } from './components/preview/preview.component';
import { ContentPipeService } from '@services/content-pipe/content-pipe.service';
import { InformationPipe } from './pipes/content/information.pipe';
import { NewsPipe } from './pipes/content/news.pipe';
import { RestaurantPipe } from './pipes/content/restaurant.pipe';
import { OpeningHoursPipe } from './pipes/content/opening-hours.pipe';
import { EventPipe } from './pipes/content/event.pipe';
import { GalleryPipe } from './pipes/content/gallery.pipe';
import { SocialMediaPipe } from './pipes/content/social-media.pipe';
import { AccommodationPipe } from './pipes/content/accommodation.pipe';
import { IbexsmartWidgetComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/forms/ibexsmart-widget/ibexsmart-widget.component';
import { SelectContactDialogComponent } from './pages/dynamic-content/dynamic-content-edit/components/contact/select-dialog/select-contact-dialog.component';
import { EditContactDialogComponent } from './pages/dynamic-content/dynamic-content-edit/components/contact/edit-dialog/edit-contact-dialog.component';
import { EditOpeningHoursDialogComponent } from './pages/dynamic-content/dynamic-content-edit/components/opening-hours/edit-dialog/edit-opening-hours-dialog.component';
import { NavigationActiveStateComponent } from './pages/navigation/navigation-active-state/navigation-active-state.component';
import { InteractivePlayoutsComponent } from './components/interactive-playouts/interactive-playouts.component';
import { StaticPlayoutsComponent } from './components/static-playouts/static-playouts.component';
import { EventDatesComponent } from './pages/dynamic-content/dynamic-content-edit/components/event-dates/event-dates.component';
import { EditEventDatesDialogComponent } from './pages/dynamic-content/dynamic-content-edit/components/event-dates/edit-dialog/edit-event-dates-dialog.component';
import { HtmlEditorComponent } from './components/editor/html-editor/html-editor/html-editor.component';
import { EditorComponent } from './components/editor/editor.component';
import { SelectAssetsComponent } from './components/select-assets/select-assets.component';
import { ScaleDirective } from './directives/scale/scale.directive';
import { HelperService } from '@services/helper/helper.service';
import { EditMediaComponent } from './pages/dynamic-content/dynamic-content-edit/components/edit-media/edit-media.component';
import { ServiceTicketComponent } from './components/service-ticket/service-ticket.component';
import { ServiceTicketDialogComponent } from './components/service-ticket-dialog/service-ticket-dialog.component';
import { IdleService } from '@services/idle/idle.service';
import { AutologoutDialogComponent } from './dialogs/autologout-dialog/autologout-dialog.component';
import { FileDragNDropDirective } from './directives/file-drag-n-drop/file-drag-n-drop.directive';
import { IntervalService } from '@services/interval/interval.service';
import { ImageEditorV2Component } from './components/image-editor-v2/image-editor-v2.component';
import { IbextabletAppComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/forms/ibextablet-app/ibextablet-app.component';
import { IbextabletWidgetComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/forms/ibextablet-widget/ibextablet-widget.component';
import { DriverComponent } from './pages/driver/driver.component';
import { DriverEditComponent } from './pages/driver/driver-edit/driver-edit.component';
import { StepDialogComponent } from './dialogs/step-dialog/step-dialog.component';
import { GroupEditFormComponent } from './pages/user-admin/user-admin-edit/forms/group-edit-form/group-edit-form.component';
import { ProfileEditFormComponent } from './pages/user-admin/user-admin-edit/forms/profile-edit-form/profile-edit-form.component';
import { AddressbookEditFormComponent } from './pages/user-admin/user-admin-edit/forms/addressbook-edit-form/addressbook-edit-form.component';
import { FrontendgroupEditFormComponent } from './pages/user-admin/user-admin-edit/forms/frontendgroup-edit-form/frontendgroup-edit-form.component';
import { FrontendprofileEditFormComponent } from './pages/user-admin/user-admin-edit/forms/frontendprofile-edit-form/frontendprofile-edit-form.component';
import { NgApexchartsModule } from 'ng-apexcharts';
import { MatPlaceholderAsteriskDirective } from './directives/require/require.directive';
import { IbexLineClampComponent } from './components/ibex-line-clamp/ibex-line-clamp.component';
import { IbexSnackbarComponent } from './components/ibex-snackbar/ibex-snackbar.component';
import { EditAssetComponent } from './pages/dynamic-content/dynamic-content-edit/components/edit-asset/edit-asset.component';
import { RippleDirective } from './directives/ripple/ripple.directive';
import { DynamicContentActionComponent } from './pages/dynamic-content/dynamic-content-action/dynamic-content-action.component';
import { AddNewToken } from './pages/user-admin/user-admin-edit/dialogs/add-new-token/add-new-token.component';
import { TickerPipe } from './pipes/content/ticker.pipe';
import { CodeSnippetEditDialogComponent } from './pages/packages/package-editor-ibextouch/code-snippet-edit-dialog/code-snippet-edit-dialog.component';
import { PackageSelectorComponent } from './components/package-selector/package-selector.component';
import { ContentPoolComponent } from './pages/content-pool/content-pool.component';
import { ContentPoolDetailComponent } from './pages/content-pool/content-pool-detail/content-pool-detail.component';
import { ContentPoolEditComponent } from './pages/content-pool/content-pool-edit/content-pool-edit.component';
import { TextFieldModule } from '@angular/cdk/text-field';
import { EditCodeSnippetDialogComponent } from './pages/dynamic-content/dynamic-content-edit/components/code-snippets/edit-dialog/edit-code-snippet-dialog.component';
import { PackageEditorIbextvComponent } from './pages/packages/package-editor-ibextv/package-editor-ibextv.component';
import { IbexTVAppComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/forms/ibextv-app/ibextv-app.component';
import { IbexTVWidgetComponent } from './pages/navigation/dialogs/ibextouch-app-dialog/forms/ibextv-widget/ibextv-widget.component';
import { ChatsComponent } from './pages/chats/chats.component';
import { ChatsListComponent } from './pages/chats/chats-list/chats-list.component';

@NgModule({
  exports: [
    DragDropModule
  ],
  declarations: [
    AddressbookEditFormComponent,
    AnalyticsComponent,
    AnalyticsDetailComponent,
    AppComponent,
    AppHighscoreResetDialogComponent,
    AppPickerDialogComponent,
    ApplicationGenerationComponent,
    AssetSelectorComponent,
    AssetsComponent,
    AuthenticationComponent,
    BannerComponent,
    BannerDetailComponent,
    BannerEditComponent,
    BarChartComponent,
    ChatsComponent,
    ChatsListComponent,
    CleanUpQuillComponent,
    CleanupCustomerComponent,
    CleanupCustomerExecuteComponent,
    CodeAreaComponent,
    CodeSnippetsComponent,
    CodeSnippetsDetailComponent,
    CodeSnippetsEditComponent,
    CodeSnippetsScreenSelectComponent,
    ColumnChartComponent,
    CompositionAppDetailComponent,
    CompositionAppDetailMenuItemsComponent,
    CompositionAppEditorComponent,
    CompositionAppEditorMenuItemsComponent,
    CompositionDetailComponent,
    CompositionImportsComponent,
    CompositionWizardCodeSnippetsComponent,
    CompositionWizardComponent,
    CompositionWizardCustomerSelectComponent,
    CompositionWizardDynamicEntranceComponent,
    CompositionWizardImportsComponent,
    CompositionWizardPackageComponent,
    CompositionWizardScreenComponent,
    CompositionsComponent,
    ConfirmDialogComponent,
    ContactComponent,
    CroppingDirective,
    CodeSnippetsContentComponent,
    ContentPoolComponent,
    ContentPoolDetailComponent,
    ContentPoolEditComponent,
    CustomerDetailComponent,
    CustomerEditComponent,
    CustomerEraserComponent,
    CustomerSelectionComponent,
    CustomerWizardComponent,
    CustomerWizardEditComponent,
    CustomersComponent,
    CustomersListComponent,
    DashboardComponent,
    InteractivePlayoutsComponent,
    DashboardDataDialogComponent,
    DashboardMessageComponent,
    DashboardMessageEditComponent,
    DashboardMessageItemComponent,
    DataTableComponent,
    DayMonthInputComponent,
    DefaultFormComponent,
    DocumentsComponent,
    DraggableDirective,
    DriverComponent,
    DriverEditComponent,
    DurationInputComponent,
    ActivityComponent,
    DynamicContentComponent,
    DynamicContentDetailComponent,
    DynamicContentEditComponent,
    DynamicContentEntranceSelectionComponent,
    DynamicContentEntrancesComponent,
    DynamicContentActionComponent,
    DynamicContentTableComponent,
    ImportantNumbersComponent,
    PersonComponent,
    EditMenuDialogComponent,
    EnsureLinkPrefixPipe,
    ErrorlogComponent,
    ErrorlogDetailComponent,
    ExternalTokensComponent,
    ExternalTokensEditComponent,
    FailedImportsComponent,
    FailedPackagesComponent,
    FailedPackagesDetailComponent,
    FailedPipeImportsComponent,
    FileSizePipe,
    FeedbackComponent,
    FinishVotingDialogComponent,
    FormDirective,
    FrontGroupEditComponent,
    FrontendgroupEditFormComponent,
    FrontendprofileEditFormComponent,
    FrontLabelEditComponent,
    FrontProfileEditComponent,
    FrontProfileExportWizardComponent,
    FrontProfileSelectionComponent,
    FrontProfilesComponent,
    FrontendErrorlogComponent,
    FrontendErrorlogDetailComponent,
    GastronomyComponent,
    GeneratorDialogComponent,
    GroupContentManagerComponent,
    GroupDetailComponent,
    GroupEditComponent,
    GroupEditFormComponent,
    GroupParticipantsManagerComponent,
    GroupRoleSelectionComponent,
    GroupRolesManagerComponent,
    GroupSecuredAppManagerComponent,
    GroupSelectionComponent,
    GroupsComponent,
    HtmlEditorComponent,
    IbexiconComponent,
    IbexCheckboxComponent,
    IbexLineClampComponent,
    IbexSnackbarComponent,
    IconSelectionDialogComponent,
    IconSelectorComponent,
    IfPrivilegeDirective,
    ImageAssetSelectorComponent,
    ImageEditorComponent,
    ImportConnectionComponent,
    ImportCreateComponent,
    ImportDataExplorerComponent,
    ImportEditComponent,
    ImportLogsComponent,
    ImportLogsDetailComponent,
    ImportTypesComponent,
    ImportTypesCreateComponent,
    ImportTypesDetailComponent,
    ImportTypesEditComponent,
    ImporterMonitorComponent,
    ImporterSelectorComponent,
    PackageSelectorComponent,
    ImportsComponent,
    ImportsDetailComponent,
    JsonAreaComponent,
    JsonContentTableComponent,
    JsonEditComponent,
    JsonReadOnlyComponent,
    JsonStringifyPipe,
    KeysPipe,
    KueComponent,
    LanguageDialogComponent,
    LanguageSelectorComponent,
    LegacyCompositionComponent,
    LegacyCompositionDetailComponent,
    LegacyCompositionEditComponent,
    LoginComponent,
    LogoComponent,
    MapValuesPipe,
    MediaComponent,
    MenuItemSelectorComponent,
    MessageBubbleComponent,
    MessageComponent,
    MyOrganizationComponent,
    MyProfileComponent,
    Nl2BrPipe,
    OpeningHoursComponent,
    OrganisationCustomerComponent,
    OrganisationCustomerDetailComponent,
    OrganisationCustomerEditComponent,
    OrganisationDetailComponent,
    OrganisationEditComponent,
    OrganisationEraserComponent,
    OrganisationSelectionComponent,
    OrganisationSelectorComponent,
    OrganisationsComponent,
    OrphansComponent,
    PackageEditorComponent,
    PackageEditorIbexinfoComponent,
    PackageEditorIbexsmartComponent,
    PackageEditorIbextabletComponent,
    PackageEditorIbextvComponent,
    PackageEditorIbextouchComponent,
    PackageEraserComponent,
    PackageGeneralComponent,
    PackageInstalledAppEditorComponent,
    PackageInstalledAppSelectionComponent,
    PackageInstalledAppsManagerComponent,
    PackageInstalledWidgetEditorComponent,
    PackageLegalComponent,
    PackageListComponent,
    PackageScreenImagesComponent,
    PackageSeasonalImagesComponent,
    PackagesComponent,
    PageActionsComponent,
    PageContentComponent,
    PageHeaderComponent,
    PageNotAuthComponent,
    PageNotFoundComponent,
    PageToolbarComponent,
    PasswordComponent,
    PasswordConfirmComponent,
    PieChartComponent,
    PipeImportDetailComponent,
    PipeImportEditComponent,
    PipeImportLogsComponent,
    PipeImportLogsDetailComponent,
    PipeImportsComponent,
    PrivilegeEditComponent,
    PrivilegesComponent,
    ProfileDetailComponent,
    ProfileEditComponent,
    ProfileEditFormComponent,
    ProfilesComponent,
    PublishingComponent,
    QrcodeDialogComponent,
    EditorComponent,
    QuizComponent,
    ReadmeComponent,
    ReadmesComponent,
    ReadmesEditComponent,
    RedirectDetailComponent,
    RedirectEditComponent,
    RedirectLogComponent,
    RedirectsComponent,
    RegisterComponent,
    ResetComponent,
    ResetRedirectComponent,
    ResourcesComponent,
    RoleDetailComponent,
    RoleEditComponent,
    RolePrivilegeManagerComponent,
    RolePrivilegeSelectionComponent,
    RolesComponent,
    SafePipe,
    ScreenDetailComponent,
    ScreenListComponent,
    ScreenSelectorComponent,
    ScreenSettingsComponent,
    ScreenSettingsEditComponent,
    SelectOrganisationComponent,
    SlideConfigComponent,
    SlideItemsComponent,
    SmartImageComponent,
    SmartInputComponent,
    SmartInputHintComponent,
    SpeedDialFabComponent,
    StepDialogComponent,
    StatusBarComponent,
    SubmenuCreatorComponent,
    TimePickerComponent,
    TimelineChartComponent,
    TitleSelectionComponent,
    TransformDatePipe,
    TranslateDatePipe,
    TranslatePipe,
    TranslationComponent,
    TranslationDetailComponent,
    TranslationEditComponent,
    UrlShortenerComponent,
    VotingComponent,
    WebcamComponent,
    UserAdminComponent,
    UserAdminDetailComponent,
    UserAdminEditComponent,
    AddItemsFromList,
    DynamicContentTableRowComponent,
    HighlightSearchPipe,
    TitleTranslateComponent,
    AddNewProfile,
    AddNewToken,
    ContactAndAddressComponent,
    ContactAndAddressEditComponent,
    SelectContactDialogComponent,
    DynamicContentTableActiveStateComponent,
    NavigationComponent,
    EditAppDialogComponent,
    CodeSnippetEditDialogComponent,
    IbextouchAppComponent,
    IbexsmartAppComponent,
    IbextouchWidgetComponent,
    IbextabletAppComponent,
    IbextabletWidgetComponent,
    IbexTVAppComponent,
    IbexTVWidgetComponent,
    IbexsmartWidgetComponent,
    PreviewComponent,
    InformationPipe,
    NewsPipe,
    RestaurantPipe,
    TickerPipe,
    OpeningHoursPipe,
    EventPipe,
    GalleryPipe,
    SocialMediaPipe,
    AccommodationPipe,
    EditContactDialogComponent,
    EditOpeningHoursDialogComponent,
    EditCodeSnippetDialogComponent,
    NavigationActiveStateComponent,
    InteractivePlayoutsComponent,
    StaticPlayoutsComponent,
    EventDatesComponent,
    EditEventDatesDialogComponent,
    SelectAssetsComponent,
    ScaleDirective,
    EditMediaComponent,
    EditAssetComponent,
    ServiceTicketComponent,
    ServiceTicketDialogComponent,
    AutologoutDialogComponent,
    FileDragNDropDirective,
    ImageEditorV2Component,
    MatPlaceholderAsteriskDirective
  ],
  bootstrap: [AppComponent], imports: [BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    NgxJsonViewerModule,
    MatSnackBarModule, MatCardModule, MatButtonModule, MatMenuModule, MatInputModule, MatOptionModule, MatCheckboxModule, MatSelectModule,
    MatSlideToggleModule, MatTabsModule, MatButtonToggleModule, MatIconModule, MatTooltipModule, MatSidenavModule, MatToolbarModule, MatListModule,
    MatDialogModule, MatStepperModule, MatAutocompleteModule, MatProgressSpinnerModule, MatProgressBarModule, MatGridListModule, MatNativeDateModule,
    MatDatepickerModule, MatExpansionModule, MatChipsModule, MatRadioModule, DragDropModule, MatPaginatorModule,
    TextFieldModule,
    MarkdownModule.forRoot({
      markedOptions: {
        provide: MERMAID_OPTIONS,
        useValue: {
          gfm: true,
          breaks: false,
          pedantic: false
        },
      },
    }),
    AngularMarkdownEditorModule.forRoot(),
    QuillModule,
    IbexIconModule,
    NgDetailViewModule.forRoot('ibexfusion'),
    NgApexchartsModule,
    RippleDirective,
  ],
  providers: [
    {
      provide: 'UPLOAD_OPTIONS',
      useValue: {}
    },
    { provide: MAT_DATE_LOCALE, useValue: 'de-DE' },
    RestService,
    AuthenticationGuard,
    OrganisationGuard,
    PrivilegeGuard,
    TokenGuard,
    DialogService,
    StorageService,
    MessengerService,
    OpenReadmeService,
    MyOrganisationService,
    PendingChangesGuard,
    TranslateService,
    EnvironmentService,
    HelperService,
    // {
    //   provide: ErrorHandler,
    //   useClass: ErrorHandlingService
    // },
    FrontendErrorService,
    ContentPipeService,
    IdleService,
    IntervalService,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
